import { LinearProgress, Stack, styled, Typography } from '@mui/material';
import React, { useMemo } from 'react';

interface StepperProgressProps {
  score: number; // Value between 0-100
}

// Styled component for custom linear progress with gradient
const GradientProgress = styled(LinearProgress)<{ startColor: string; endColor: string }>(
  ({ startColor, endColor }) => ({
    height: 4,
    borderRadius: 4,
    backgroundColor: '#e0e0e0',
    '.MuiLinearProgress-bar': {
      background: `linear-gradient(90deg, ${startColor} 0%, ${endColor} 100%)`,
      transition: 'transform 0.8s ease',
    },
  })
);

// SVG Circle component
const ProgressCircle: React.FC<{ color: string }> = ({ color }) => (
  <svg width="18" height="18" viewBox="0 0 18 18" style={{ zIndex: 99 }}>
    <circle cx="9" cy="9" r="9" fill={color} />
  </svg>
);

const StepperProgress: React.FC<StepperProgressProps> = ({ score }) => {
  const clampedScore = Math.max(0, Math.min(100, score));

  const getTitle = () => {
    if (clampedScore <= 30) {
      return "Major changes needed ❗"
    } else if (clampedScore <= 45) {
      return "Some changes needed 🚧"
    } else if (clampedScore <= 70) {
      return "Minor adjustments needed 🔧"
    } else {
      return "Good to go 🚀"
    }
  }

  const getTitleColor = () => {
    if (clampedScore <= 30) {
      return "#ff4444"
    } else if (clampedScore <= 70) {
      return "#ffbb33"
    }
    return "#00C851"
  }

  // Calculate progress values for each section
  const { section1Progress, section2Progress, section3Progress, colors } = useMemo(() => {
    // Define colors
    const red = '#ff4444';
    const yellow = '#ffbb33';
    const green = '#00C851';

    let section1Progress = 0;
    let section2Progress = 0;
    let section3Progress = 0;

    // Calculate progress for each section based on score
    if (clampedScore <= 30) {
      section1Progress = (clampedScore / 30) * 100;
    } else if (clampedScore <= 70) {
      section1Progress = 100;
      section2Progress = (clampedScore - 30) / 40 * 100;
    } else {
      section1Progress = 100;
      section2Progress = 100;
      section3Progress = (clampedScore - 70) / 30 * 100;
    }

    return {
      section1Progress,
      section2Progress,
      section3Progress,
      colors: {
        section1Start: red,
        circle1: yellow,
        circle2: green,
        section3End: green
      }
    };
  }, [clampedScore]);

  return (
    <Stack direction='column' spacing={1} alignItems='center'>
      <Stack
        direction="row"
        alignItems="center"
        spacing={-0.5}
        sx={{ width: '100%', maxWidth: 600, my: 2 }}
      >
        <GradientProgress
          variant="determinate"
          value={section1Progress}
          startColor={colors.section1Start}
          endColor={colors.circle1}
          sx={{ flex: 3 }}
        />
        <ProgressCircle color={clampedScore <= 30 ? '#e0e0e0' : colors.circle1} />
        <GradientProgress
          variant="determinate"
          value={section2Progress}
          startColor={colors.circle1}
          endColor={colors.circle1}
          sx={{ flex: 4 }}
        />
        <ProgressCircle color={clampedScore <= 70 ? '#e0e0e0' : colors.circle2} />
        <GradientProgress
          variant="determinate"
          value={section3Progress}
          startColor={colors.circle2}
          endColor={colors.section3End}
          sx={{ flex: 3 }}
        />
      </Stack>
      <Typography variant="h6" color={getTitleColor()}>
        {getTitle()}
      </Typography>
    </Stack>
  );
};

export default StepperProgress; 